// @flow
import type { StoreState } from 'reducers';

import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import useCurrencyConfigs from './useCurrencyConfigs';
import {
  blockSizeFor,
  formatAmount,
  getMigratedDisplayAmount,
  getMigratedRawAmount,
  getMigratedStepMajor,
  isDivisibleBy,
  powerOfTenDecimals,
  selectConfigForCountry,
  toGrpAmount,
} from './currencyConfig';

import './CurrencyAmountPreview.scss';

type Props = {
  amount: number | string,
  fromCurrency: string,
  flowType: string,
  country: ?string,
  appState: Object,
  onValidityChange?: (isValid: boolean, errorMessage: ?string) => void,
  // Reports whether the underlying currency-config fetch itself failed (as
  // opposed to the block-size check failing). Never blocks on its own — the
  // component still fails open — but lets a parent page show its own
  // indicator (e.g. a persistent banner) for the fact that GrabCoins
  // verification is currently unavailable, on top of the Toastr this
  // component already raises.
  onLoadError?: (hasError: boolean) => void,
};

// Human-readable reason the migrated amount is invalid, derived from the block
// size granularity: whole number, N decimal places, or a non-decimal multiple.
function roundingRequirement(config, flowType): string {
  const step = getMigratedStepMajor(config, flowType);
  const decimals = powerOfTenDecimals(step);
  if (decimals === 0) return i18n.t('currency_preview.block_size_requirement_whole');
  if (decimals === 1) return i18n.t('currency_preview.block_size_requirement_decimal_one');
  if (decimals != null) {
    return i18n.t('currency_preview.block_size_requirement_decimal_other', { count: decimals });
  }
  return i18n.t('currency_preview.block_size_requirement_multiple', { step: formatAmount(step) });
}

export function CurrencyAmountPreview(props: Props) {
  const { amount, fromCurrency, flowType, country, appState, onValidityChange, onLoadError } = props;
  const { configs, error } = useCurrencyConfigs(appState);

  const lastReportedLoadError = useRef(null);
  useEffect(() => {
    if (!onLoadError) return;
    if (lastReportedLoadError.current === error) return;
    lastReportedLoadError.current = error;
    onLoadError(error);
  }, [onLoadError, error]);

  const isAmountEntered = amount !== null && amount !== undefined && amount !== '';

  const preview = useMemo(() => {
    if (!configs || !isAmountEntered) return null;
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) return null;

    const grpAmount = toGrpAmount(numericAmount, fromCurrency, configs);
    // fromCurrency has no known conversion rate (e.g. an OVO-POINT reward
    // before Indonesia is rolled out to Uno) — render nothing rather than a
    // number computed against the wrong currency.
    if (grpAmount === null) return null;

    // Country has no Uno rollout (or none was supplied) — nothing to preview.
    const config = selectConfigForCountry(configs, country);
    if (!config) return null;

    const valid = isDivisibleBy(grpAmount, blockSizeFor(config, flowType));
    return {
      grpAmount,
      valid,
      migratedAmount: getMigratedDisplayAmount(config, grpAmount),
      migratedRawAmount: getMigratedRawAmount(config, grpAmount),
      requirement: valid ? null : roundingRequirement(config, flowType),
    };
  }, [configs, isAmountEntered, amount, fromCurrency, flowType, country]);

  // Only a rendered block-size failure is blocking; every other state (loading,
  // fetch error, no amount, country not rolled out, valid amount) is treated as
  // valid so the parent form is never blocked on a non-error condition.
  const errorMessage = (preview && !preview.valid)
    ? i18n.t('currency_preview.block_size_error', {
      amount: preview.migratedRawAmount,
      requirement: preview.requirement,
    })
    : null;
  const isValid = errorMessage === null;
  const lastReported = useRef({ isValid: null, errorMessage: undefined });
  useEffect(() => {
    if (!onValidityChange) return;
    if (lastReported.current.isValid === isValid && lastReported.current.errorMessage === errorMessage) return;
    lastReported.current = { isValid, errorMessage };
    onValidityChange(isValid, errorMessage);
  }, [onValidityChange, isValid, errorMessage]);

  if (error) return null;
  if (!configs) return <div className="currencyPreview">{i18n.t('currency_preview.loading')}</div>;
  if (!preview) return null;

  return (
    <div className="currencyPreview">
      {preview.valid ? (
        <div className="currencyPreview__line">
          {i18n.t('currency_preview.migrated_users', { amount: preview.migratedAmount })}
        </div>
      ) : (
        <div className="currencyPreview__error">
          {errorMessage}
        </div>
      )}
      <div className="currencyPreview__line">
        {i18n.t('currency_preview.non_migrated_users', { amount: preview.grpAmount })}
      </div>
    </div>
  );
}

function mapStateToProps(state: StoreState) {
  return { appState: state.app };
}

export default connect(mapStateToProps)(CurrencyAmountPreview);
